import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuthenticatedApi } from './api';
import ForumPostComments from './components/ForumPostComments';
import { getAuthorName } from './utils/authorName';
import './ForumPost.css';

export default function ThreadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const { authenticatedFetch } = useAuthenticatedApi();
  
  const [thread, setThread] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [totalLikes, setTotalLikes] = useState(0);

  // Helper to check if author should use Study Hall logo
  const shouldUseLogo = (authorName) => {
    const name = authorName.toLowerCase().trim();
    return name === "'study hall' via study hall listserv" || name === "study hall admin";
  };

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Function to update only the points count
  const updatePointsCount = async () => {
    try {
      const pointsResponse = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/forum/${id}/points`);
      if (pointsResponse.ok) {
        const pointsData = await pointsResponse.json();
        setTotalLikes(pointsData.totalPoints || 0);
      }
    } catch (err) {
      console.error('Error fetching thread points:', err);
    }
  };

  // Function to add a new comment to the posts array without refetching
  const addNewComment = (newComment) => {
    setPosts(prevPosts => {
      // Create a new comment object with the structure expected by the component
      const commentPost = {
        id: newComment.id,
        content: newComment.content,
        member_id: newComment.member_id,
        parent_post_id: newComment.parent_post_id,
        created_at: newComment.created_at,
        author_name: newComment.author_name,
        first_name: newComment.first_name,
        last_name: newComment.last_name,
        display_name: newComment.display_name,
        profile_picture: newComment.profile_picture,
        profile_picture_type: newComment.profile_picture_type,
        children: []
      };
      
      // Add the new comment to the array
      return [...prevPosts, commentPost];
    });
  };

  // Fetch thread and posts
  const fetchThread = async () => {
    try {
      setLoading(true);
      setErrorMessage(null);

      const url = `${process.env.REACT_APP_API_URL || '/api'}/forum/${id}/posts`;

      const response = await authenticatedFetch(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData?.error?.message || errorData?.message || (response.status === 404 ? 'Thread not found' : 'Failed to fetch thread');
        setErrorMessage(message);
        return;
      }

      const result = await response.json();

      setThread(result.thread);
      setPosts(result.posts);

      // Fetch total points
      await updatePointsCount();
    } catch (err) {
      console.error('❌ Fetch error:', err);
      setErrorMessage(err.message || 'Failed to fetch thread');
    } finally {
      setLoading(false);
    }
  };

  // Fetch thread and posts
  useEffect(() => {
    if (isAuthenticated && id) {
      fetchThread();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, id]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) {
      return 'just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days === 1 ? '' : 's'} ago`;
    } else if (diffInSeconds < 31536000) {
      const months = Math.floor(diffInSeconds / 2592000);
      return `${months} month${months === 1 ? '' : 's'} ago`;
    } else {
      const years = Math.floor(diffInSeconds / 31536000);
      return `${years} year${years === 1 ? '' : 's'} ago`;
    }
  };

  const processHtmlContent = (htmlContent) => {
    if (!htmlContent) return '';

    const s = String(htmlContent);

    // If the content already contains markup, treat it as HTML and only harden links.
    if (/[<>]/.test(s)) {
      return s.replace(/<a\s+([^>]*?)>/gi, (match, attributes) => {
        if (attributes.includes('target=')) return match;
        return `<a ${attributes} target="_blank" rel="noopener noreferrer">`;
      });
    }

    // Otherwise, treat as plain text: escape + linkify URLs + preserve newlines.
    const escapeHtml = (t) =>
      String(t)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const escaped = escapeHtml(s);
    const urlRegex = /\bhttps?:\/\/[^\s<]+/gi;
    const linkified = escaped.replace(urlRegex, (url) => {
      // url is already escaped; safe to reuse for href and text
      return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
    });
    return linkified.replace(/\n/g, '<br/>');
  };

  // Build nested structure from flat posts array
  const buildNestedPosts = (posts) => {
    if (posts.length === 0) return [];
    
    const postMap = new Map();
    const rootPosts = [];
    
    // Find the first post (original thread post)
    const firstPost = posts.find(post => !post.parent_post_id);

    // First pass: create map of all posts
    posts.forEach(post => {
      postMap.set(post.id, { ...post, children: [] });
    });

    // Second pass: build hierarchy
    posts.forEach(post => {
      if (post.parent_post_id && postMap.has(post.parent_post_id)) {
        // Check if this is a reply to the first post (should be top-level)
        if (firstPost && post.parent_post_id === firstPost.id) {
          // Reply to first post = top-level post
          rootPosts.push(postMap.get(post.id));
        } else {
          // This is a reply to another post - add it as a child
          postMap.get(post.parent_post_id).children.push(postMap.get(post.id));
        }
      } else {
        // This is a root-level post (original thread post)
        rootPosts.push(postMap.get(post.id));
      }
    });

          // Flatten structure after level 3
          const flattenAfterLevel3 = (posts, currentLevel = 0) => {
            const result = [];
            
            posts.forEach(post => {
              result.push(post);
              
              if (post.children && post.children.length > 0) {
                if (currentLevel < 3) {
                  // Keep nesting for levels 0, 1, and 2
                  post.children = flattenAfterLevel3(post.children, currentLevel + 1);
                } else {
                  // Flatten levels 3+ - move all children to current level
                  const flattenedChildren = flattenAfterLevel3(post.children, currentLevel);
                  result.push(...flattenedChildren);
                  post.children = []; // Clear children since they're now at same level
                }
              }
            });
            
            return result;
          };

    // Apply flattening to root posts
    const flattenedRootPosts = flattenAfterLevel3(rootPosts);

    // Sort by creation date to maintain chronological order
    flattenedRootPosts.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    return flattenedRootPosts;
  };

  // Render a single post (without children)
  const renderPost = (post, depth = 0, isDirectChild = false, hasPreviousChildren = false) => {
    // Cap depth at 3 levels maximum for CSS classes
    const level = Math.min(depth, 3);
    
    // Calculate marginTop based on the conditions
    let marginTop;
    if (level === 0 && hasPreviousChildren) {
      // Top-level posts after a parent with children get extra spacing
      marginTop = '3rem';
    } else {
      // All other posts (top-level and non-top-level) get minimal spacing
      marginTop = '0';
    }
    
    // Force inline styles to override any CSS issues
    const inlineStyle = {
      backgroundColor: level === 0 ? 'white' : 'transparent', // No background for non-top-level posts
      borderRadius: level === 0 ? '8px' : '0', // No border radius for non-top-level posts
      padding: level === 0 ? undefined : '0', // No padding for non-top-level posts
      boxShadow: level === 0 ? '0 2px 4px rgba(0,0,0,0.1)' : 'none', // No shadow for non-top-level posts
      border: level === 0 ? '1px solid #999999' : 'none', // No border for non-top-level posts
      marginBottom: '0', // No spacing between replies
      marginLeft: level === 0 ? '0' : `${level * 30}px`, // Progressive indentation: 30px, 60px, 90px
      borderLeft: level === 0 ? '1px solid #999999' : 'none', // No left border for non-top-level posts
      paddingLeft: level === 0 ? undefined : '0', // No left padding for non-top-level posts
      marginTop: marginTop,
      display: 'block' // Always use block layout
    };
    
    return (
      <div key={post.id} className={`forum-post level-${level}`} style={inlineStyle}>
        {level === 0 ? (
          // Top-level post layout (original layout)
          <>
            {/* Post Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1rem',
              paddingBottom: '0.75rem',
              borderBottom: '1px solid #e9ecef'
            }}>
              {/* Author Avatar */}
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '4px',
                backgroundColor: shouldUseLogo(getAuthorName(post)) ? 'transparent' : '#38b44a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: '600',
                fontSize: '1rem',
                flexShrink: 0,
                overflow: 'hidden'
              }}>
                {shouldUseLogo(getAuthorName(post)) ? (
                  <img 
                    src="/logo.png"
                    alt="Study Hall"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '4px'
                    }}
                  />
                ) : post.profile_picture ? (
                  <img 
                    src={`data:${post.profile_picture_type || 'image/jpeg'};base64,${post.profile_picture}`}
                    alt={getAuthorName(post)}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '4px'
                    }}
                  />
                ) : (
                  getAuthorName(post).charAt(0).toUpperCase()
                )}
              </div>

              {/* Author Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '0.25rem'
                }}>
                  {post.member_id ? (
                    <div 
                      style={{ 
                        fontSize: '1rem', 
                        fontWeight: '600', 
                        color: '#38b44a',
                        cursor: 'pointer',
                        textDecoration: 'none'
                      }}
                      onClick={() => navigate(`/profile/${post.member_id}`)}
                      onMouseOver={(e) => {
                        e.target.style.textDecoration = 'underline';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.textDecoration = 'none';
                      }}
                    >
                      {getAuthorName(post)}
                    </div>
                  ) : (
                    <div style={{ 
                      fontSize: '1rem', 
                      fontWeight: '600', 
                      color: '#38b44a'
                    }}>
                      {getAuthorName(post)}
                    </div>
                  )}
                  <div style={{ 
                    fontSize: '0.8rem', 
                    color: '#666'
                  }}>
                    {formatDateAgo(post.created_at)}
                  </div>
                </div>
              </div>
            </div>

            {/* Post Content */}
            <div
              style={{
                fontSize: '1rem',
                lineHeight: '1.6',
                color: '#333',
                width: '100%',
                maxWidth: '100%',
                overflow: 'auto'
              }}
            >
              {thread.type === 'plug-your-work' && thread.plug_your_work_url && !post.parent_post_id ? (
                <div style={{ margin: '0 0 10px', fontSize: '0.95rem', lineHeight: 1.35 }}>
                  <a
                    href={thread.plug_your_work_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#38b44a', textDecoration: 'underline', wordBreak: 'break-word' }}
                  >
                    {thread.plug_your_work_url}
                  </a>
                </div>
              ) : null}
              <div 
                style={{
                  width: '100%',
                  maxWidth: '100%',
                  overflow: 'auto'
                }}
                dangerouslySetInnerHTML={{ __html: processHtmlContent(post.content) }} 
              />
            </div>
          </>
        ) : (
          // Non-top-level post layout (new compact layout)
          <>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
              {/* Author Avatar - standalone on the left */}
              <div style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                backgroundColor: shouldUseLogo(getAuthorName(post)) ? 'transparent' : '#38b44a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: '600',
                fontSize: '0.8rem',
                flexShrink: 0,
                overflow: 'hidden'
              }}>
                {shouldUseLogo(getAuthorName(post)) ? (
                  <img 
                    src="/logo.png"
                    alt="Study Hall"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '50%'
                    }}
                  />
                ) : post.profile_picture ? (
                  <img 
                    src={`data:${post.profile_picture_type || 'image/jpeg'};base64,${post.profile_picture}`}
                    alt={getAuthorName(post)}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '50%'
                    }}
                  />
                ) : (
                  getAuthorName(post).charAt(0).toUpperCase()
                )}
              </div>

              {/* Content section with background */}
              <div style={{
                flex: 1,
                minWidth: 0,
                maxWidth: 'calc(100% - 60px)', // Ensure it doesn't exceed available space
                backgroundColor: '#e8f4f8',
                borderRadius: '8px',
                padding: '0.5rem',
                border: '1px solid #d1ecf1',
                overflow: 'hidden'
              }}>
                {/* Author Info */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '0'
                }}>
                  {post.member_id ? (
                    <div 
                      style={{ 
                        fontSize: '0.9rem', 
                        fontWeight: '600', 
                        color: '#38b44a',
                        cursor: 'pointer',
                        textDecoration: 'none'
                      }}
                      onClick={() => navigate(`/profile/${post.member_id}`)}
                      onMouseOver={(e) => {
                        e.target.style.textDecoration = 'underline';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.textDecoration = 'none';
                      }}
                    >
                      {getAuthorName(post)}
                    </div>
                  ) : (
                    <div style={{ 
                      fontSize: '0.9rem', 
                      fontWeight: '600', 
                      color: '#38b44a'
                    }}>
                      {getAuthorName(post)}
                    </div>
                  )}
                  <div style={{ 
                    fontSize: '0.8rem', 
                    color: '#666'
                  }}>
                    {formatDateAgo(post.created_at)}
                  </div>
                </div>

                {/* Post Content */}
                <div 
                  style={{
                    fontSize: '0.9rem',
                    lineHeight: '1.4',
                    color: '#333',
                    marginTop: '5px', // Add 5px spacing between author info and message text
                    width: '100%',
                    maxWidth: '100%',
                    overflow: 'auto'
                  }}
                >
                  <div 
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      overflow: 'auto'
                    }}
                    dangerouslySetInnerHTML={{ __html: processHtmlContent(post.content) }} 
                  />
                </div>
              </div>
            </div>

            {/* Reply button outside content background */}
            <div style={{
              marginTop: '0',
              marginLeft: '36px', // Align with content section
              marginBottom: '0'
            }}>
              <ForumPostComments 
                contentId={post.id} 
                isFirstPost={!post.parent_post_id}
                ownerId={post.member_id}
                onCommentAdded={(newComment) => {
                  addNewComment(newComment);
                }}
                onReactionChange={async () => {
                  // Update only the points count, not the entire thread
                  await updatePointsCount();
                }}
              />
            </div>
          </>
        )}
        
        {/* Comments for top-level posts */}
        {level === 0 && (
          <div style={{ marginTop: '1rem' }}>
            <ForumPostComments 
              contentId={post.id} 
              isFirstPost={!post.parent_post_id}
              ownerId={post.member_id}
              onCommentAdded={(newComment) => {
                addNewComment(newComment);
              }}
              onReactionChange={async () => {
                // Update only the points count, not the entire thread
                await updatePointsCount();
              }}
            />
          </div>
        )}
      </div>
    );
  };

  // Render posts with proper nesting structure
  const renderNestedPosts = (posts) => {
    const nestedPosts = buildNestedPosts(posts);
    const result = [];
    
    const renderPostAndChildren = (post, depth = 0, isDirectChild = false, hasPreviousChildren = false) => {
      // Render the current post with context about being a direct child and if previous post had children
      result.push(renderPost(post, depth, isDirectChild, hasPreviousChildren));
      
      // Track if this post has children for the next post
      const hasChildren = post.children && post.children.length > 0;
      
      // Render children if they exist
      if (hasChildren) {
        post.children.forEach((child, index) => {
          // First child is always a direct child of its parent
          const isFirstChild = index === 0;
          renderPostAndChildren(child, depth + 1, isFirstChild, false);
        });
      }
      
      return hasChildren;
    };
    
    let previousHadChildren = false;
    nestedPosts.forEach((post, index) => {
      // First post at root level is not a direct child
      const isFirstRootPost = index === 0;
      const hasChildren = renderPostAndChildren(post, 0, !isFirstRootPost, previousHadChildren);
      previousHadChildren = hasChildren;
    });
    
    return result;
  };

  if (authLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        fontSize: '1.2rem',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        fontSize: '1.2rem',
        color: '#666'
      }}>
        Loading thread...
      </div>
    );
  }

  if (!thread) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        fontSize: '1.2rem',
        color: '#666',
        textAlign: 'center',
        padding: '2rem'
      }}>
        {errorMessage || 'Thread not found'}
      </div>
    );
  }

  return (
    <div style={{ 
      maxWidth: '1000px', 
      margin: '0 auto', 
      padding: '1.5rem',
      backgroundColor: '#f8f9fa',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <button 
          onClick={() => navigate('/forum')}
          style={{
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            marginBottom: '1rem',
            fontSize: '0.9rem'
          }}
        >
          ← Back to Forum
        </button>
        
        <h1 style={{ 
          fontSize: '1.8rem', 
          fontWeight: '700', 
          color: '#333', 
          marginBottom: '0.5rem',
          lineHeight: '1.3'
        }}>
          {thread.subject}
        </h1>
        
        <div style={{ 
          fontSize: '0.9rem', 
          color: '#666',
          display: 'flex',
          gap: '1rem',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <span>
            {totalLikes} {Math.abs(totalLikes) === 1 ? 'point' : 'points'}
          </span>
          <span>•</span>
          <span>{posts.length} {posts.length === 1 ? 'post' : 'posts'}</span>
          <span>•</span>
          <span>Started {formatDate(thread.created_at)}</span>
        </div>
      </div>

      {/* Posts */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {renderNestedPosts(posts)}
      </div>


      {/* Footer */}
      <div style={{
        marginTop: '3rem',
        padding: '1rem',
        textAlign: 'center',
        color: '#666',
        fontSize: '0.9rem'
      }}>
        End of thread • {posts.length} {posts.length === 1 ? 'post' : 'posts'}
      </div>
    </div>
  );
}
